import { createFileRoute, Link } from '@tanstack/react-router';
import { JournalEntryForm } from '../../components/journal/JournalEntryForm';
import { useJournalEntry } from '../../hooks/useJournalEntries';
import { useActiveCompanyId } from '../../stores/useCompanyStore';

export const Route = createFileRoute('/_auth/journal/$id_/edit')({
  component: EditJournalEntryPage,
});

function EditJournalEntryPage() {
  const { id } = Route.useParams();
  const companyId = useActiveCompanyId();
  const { data: entry, isLoading, error } = useJournalEntry(companyId, id);

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <p className="muted">No active company.</p>
      </div>
    );
  }
  if (isLoading) return <p className="muted">Loading…</p>;
  if (error || !entry) return <p className="field-error">Failed to load entry.</p>;

  const editableSource = entry.source === 'manual' || entry.source === 'import';
  // Voided entries are still off-limits — you have to un-void first, and we
  // don't provide that. Non-manual sources are also off-limits — they have
  // their own editors. Locked entries are editable via owner override.
  const editable = editableSource && !entry.isVoid;
  if (!editable) {
    const why = entry.isVoid
      ? 'it has been voided'
      : `it was created by ${entry.source} — edit it through its source document`;
    return (
      <div>
        <Link to="/journal/$id" params={{ id }} style={{ fontSize: 14 }}>
          ← Back to {entry.entryNumber}
        </Link>
        <div className="card" style={{ padding: 20, marginTop: 8 }}>
          <p style={{ marginTop: 0 }}>This entry can’t be edited because {why}.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Link to="/journal/$id" params={{ id }} style={{ fontSize: 14 }}>
        ← Back to {entry.entryNumber}
      </Link>
      <h1 style={{ fontSize: 24, margin: '8px 0 16px' }}>Edit {entry.entryNumber}</h1>
      {entry.isLocked && (
        <div
          className="card"
          style={{
            background: 'var(--tag-warn-bg)',
            color: 'var(--tag-warn-fg)',
            borderColor: 'var(--tag-warn-fg)',
            marginBottom: 16,
            fontSize: 13,
          }}
        >
          <strong>This entry is locked</strong> by a completed reconciliation. Saving your
          edits will bypass the lock (owner override) and detach this entry from the
          reconciliation. The reconciliation record + hash chain are preserved for audit,
          but the recon math will no longer tie out — you may want to void and redo it.
          A reason is required and logged for audit.
        </div>
      )}
      <JournalEntryForm
        companyId={companyId}
        initialEntry={entry}
        editEntryId={id}
        forceOverride={entry.isLocked}
      />
    </div>
  );
}
